/**
 * DashAuctions — Phase 7 (Business Dashboard)
 * Manage business's auctions (create, cancel, view analytics).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Plus, RefreshCw, TrendingUp, X } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "../../../lib/i18n";
import { auctionsAPI, AUCTION_STATUS_LABELS, formatCountdown } from "../../../lib/auctionsV2";

const TABS = [
  { key: "",          ar: "الكل",     en: "All"       },
  { key: "live",      ar: "مباشر",    en: "Live"      },
  { key: "scheduled", ar: "مجدول",    en: "Scheduled" },
  { key: "sold",      ar: "مبيع",     en: "Sold"      },
  { key: "unsold",    ar: "لم يُبع", en: "Unsold"    },
];

export default function DashAuctions({ businessId }) {
  const { lang } = useI18n();
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    title_ar: "", starting_price_sar: "", reserve_price_sar: "",
    buy_now_price_sar: "", bid_increment_sar: "500", ends_at: "",
    auction_type: "timed", description_ar: "",
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, ax] = await Promise.all([
        auctionsAPI.list({ business_id: businessId, status: status || undefined, limit: 100 }),
        auctionsAPI.analytics(businessId).catch(() => null),
      ]);
      setData(d);
      setAnalytics(ax);
    } catch { toast.error(lang === "ar" ? "تعذّر التحميل" : "Failed"); }
    finally { setLoading(false); }
  }, [businessId, status, lang]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title_ar || !form.starting_price_sar || !form.ends_at) return;
    setCreating(true);
    try {
      const payload = {
        business_id: businessId,
        title_ar: form.title_ar,
        description_ar: form.description_ar || undefined,
        starting_price_sar: Number(form.starting_price_sar),
        reserve_price_sar: form.reserve_price_sar ? Number(form.reserve_price_sar) : undefined,
        buy_now_price_sar: form.buy_now_price_sar ? Number(form.buy_now_price_sar) : undefined,
        bid_increment_sar: Number(form.bid_increment_sar) || 500,
        auction_type: form.auction_type,
        ends_at: new Date(form.ends_at).toISOString(),
      };
      await auctionsAPI.create(payload);
      toast.success(lang === "ar" ? "تم إنشاء المزاد" : "Auction created");
      setShowCreate(false);
      setForm({ ...form, title_ar: "", starting_price_sar: "", ends_at: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل" : "Failed"));
    } finally { setCreating(false); }
  };

  const cancel = async (a) => {
    if (!window.confirm(lang === "ar" ? "إلغاء المزاد؟" : "Cancel auction?")) return;
    try {
      await auctionsAPI.cancel(a.id, "seller_cancel");
      toast.success(lang === "ar" ? "تم الإلغاء" : "Canceled");
      await load();
    } catch { toast.error(lang === "ar" ? "فشل" : "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="dash-auctions">
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <MetricBox lbl={lang === "ar" ? "مباشر" : "Active"} val={analytics.active_auctions} />
          <MetricBox lbl={lang === "ar" ? "مجدول" : "Scheduled"} val={analytics.scheduled_auctions} />
          <MetricBox lbl={lang === "ar" ? "منتهي" : "Ended"} val={analytics.ended_auctions} />
          <MetricBox lbl={lang === "ar" ? "مبيع" : "Sold"} val={analytics.sold_auctions} />
          <MetricBox lbl={lang === "ar" ? "الإيرادات (ر.س)" : "Revenue"} val={(analytics.revenue_sar || 0).toLocaleString()} />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map((t) => (
          <button type="button" key={t.key || "all"} onClick={() => setStatus(t.key)}
                  data-testid={`auc-tab-${t.key || "all"}`}
                  className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold ${
                    status === t.key ? "bg-[#0a0a0a] text-[#f7eecd]" : "bg-white border border-[#0a0a0a]/10 text-[#525252]"
                  }`}>
            {lang === "ar" ? t.ar : t.en}
          </button>
        ))}
        <button type="button" onClick={() => setShowCreate(true)}
                data-testid="auc-create-btn"
                className="ms-auto inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[12px] font-bold text-[#0a0a0a]"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}>
          <Plus className="w-3.5 h-3.5" /> {lang === "ar" ? "مزاد جديد" : "New Auction"}
        </button>
        <button type="button" onClick={load} className="p-1.5 rounded-full hover:bg-[#0a0a0a]/[0.06]"
                data-testid="auc-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>
      ) : data.items.length === 0 ? (
        <div className="p-10 text-center rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10"
             data-testid="dash-auc-empty">
          <Gavel className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div className="text-sm text-[#8a8a8a]">{lang === "ar" ? "لا توجد مزادات" : "No auctions"}</div>
        </div>
      ) : (
        <div className="grid gap-2">
          {data.items.map((a) => {
            const lbl = AUCTION_STATUS_LABELS[a.status] || {};
            const live = ["live", "extended"].includes(a.status);
            return (
              <div key={a.id} className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4 flex items-center gap-3 flex-wrap"
                   data-testid={`biz-auc-${a.id}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="text-[13px] font-bold text-[#0a0a0a]">{a.title_ar}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0a0a0a] text-[#f7eecd]">
                      {lang === "ar" ? lbl.ar : lbl.en}
                    </span>
                    {live && (
                      <span className="text-[10px] text-red-600 inline-flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {formatCountdown(a.ends_at)}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-[#525252]">
                    {a.bid_count} {lang === "ar" ? "مزايدة" : "bids"} · {(a.current_price_sar || 0).toLocaleString()} ر.س
                  </div>
                </div>
                <Link to={`/auctions-v2/${a.id}`}
                      className="px-3 py-1.5 rounded-full text-[11px] font-bold border border-[#0a0a0a]/15 hover:border-[#c9a55c]">
                  {lang === "ar" ? "عرض" : "View"}
                </Link>
                {["draft", "scheduled", "live", "extended"].includes(a.status) && (
                  <button type="button" onClick={() => cancel(a)}
                          className="px-3 py-1.5 rounded-full text-[11px] font-bold text-red-700 bg-red-50 border border-red-200"
                          data-testid={`biz-auc-cancel-${a.id}`}>
                    {lang === "ar" ? "إلغاء" : "Cancel"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <>
          <button type="button" aria-label="close" onClick={() => setShowCreate(false)}
                  className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-8 bottom-8 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-14 sm:max-w-lg z-50 rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
               data-testid="auc-create-modal">
            <div className="p-5 border-b border-[#0a0a0a]/[0.06] flex items-center justify-between">
              <h3 className="text-lg font-bold">{lang === "ar" ? "مزاد جديد" : "New Auction"}</h3>
              <button type="button" onClick={() => setShowCreate(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={create} className="p-5 space-y-3 overflow-y-auto">
              <Field label={lang === "ar" ? "العنوان" : "Title"}>
                <input type="text" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })}
                       className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" required
                       data-testid="auc-title" />
              </Field>
              <Field label={lang === "ar" ? "الوصف" : "Description"}>
                <textarea rows={2} value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={lang === "ar" ? "نوع المزاد" : "Type"}>
                  <select value={form.auction_type} onChange={(e) => setForm({ ...form, auction_type: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white">
                    <option value="timed">{lang === "ar" ? "زمني" : "Timed"}</option>
                    <option value="scheduled">{lang === "ar" ? "مجدول" : "Scheduled"}</option>
                    <option value="live">{lang === "ar" ? "مباشر" : "Live"}</option>
                  </select>
                </Field>
                <Field label={lang === "ar" ? "ينتهي في" : "Ends at"}>
                  <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" required
                         data-testid="auc-ends-at" />
                </Field>
                <Field label={lang === "ar" ? "السعر الابتدائي" : "Starting price"}>
                  <input type="number" value={form.starting_price_sar} onChange={(e) => setForm({ ...form, starting_price_sar: e.target.value })}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" required
                         data-testid="auc-start-price" />
                </Field>
                <Field label={lang === "ar" ? "زيادة العرض" : "Bid increment"}>
                  <input type="number" value={form.bid_increment_sar} onChange={(e) => setForm({ ...form, bid_increment_sar: e.target.value })}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
                </Field>
                <Field label={lang === "ar" ? "الحد الأدنى (Reserve)" : "Reserve price"}>
                  <input type="number" value={form.reserve_price_sar} onChange={(e) => setForm({ ...form, reserve_price_sar: e.target.value })}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
                </Field>
                <Field label={lang === "ar" ? "الشراء الفوري" : "Buy now"}>
                  <input type="number" value={form.buy_now_price_sar} onChange={(e) => setForm({ ...form, buy_now_price_sar: e.target.value })}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
                </Field>
              </div>
              <div className="flex justify-end gap-2 pt-1 sticky bottom-0 bg-white">
                <button type="button" onClick={() => setShowCreate(false)}
                        className="px-4 py-2 rounded-full text-sm text-[#525252]">
                  {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
                <button type="submit" disabled={creating}
                        className="px-4 py-2 rounded-full text-sm font-bold text-[#0a0a0a] disabled:opacity-40"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                        data-testid="auc-create-submit">
                  {creating ? "…" : (lang === "ar" ? "إنشاء" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function MetricBox({ lbl, val }) {
  return (
    <div className="rounded-xl bg-white border border-[#0a0a0a]/[0.06] p-3 text-center">
      <div className="text-lg font-bold text-[#0a0a0a]">{val}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#8a6f2e]">{lbl}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">{label}</label>
      {children}
    </div>
  );
}
